import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { modalURLFromLocation, createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
import { useTranslation } from 'react-i18next';
import { PATH_USER_VIDEOS, PATH_USER_MODERATE, PATH_SIGN_IN } from '@jwp/ott-common/src/paths';

import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import Welcome from '../../components/Welcome/Welcome';
import PaymentPopup from '../../components/PaymentPopup/PaymentPopup';
import Dialog from '../../components/Dialog/Dialog';
import DeleteAccountModal from '../../components/DeleteAccountModal/DeleteAccountModal';
import ExportData from '../../components/ExportData/ExportData';
import FinalizePayment from '../../components/FinalizePayment/FinalizePayment';
import WaitingForPayment from '../../components/WaitingForPayment/WaitingForPayment';
import UpgradeSubscription from '../../components/UpgradeSubscription/UpgradeSubscription';
import DeleteAccountPasswordWarning from '../../components/DeleteAccountPasswordWarning/DeleteAccountPasswordWarning';
import UpdatePaymentMethod from '../UpdatePaymentMethod/UpdatePaymentMethod';
import ShowMoreDetails from '../../components/ShowMoreDetails/ShowMoreDetails';
import RejectReasonModal from '../../components/RejectReasonModal/RejectReasonModal';
import WarningHasSubscription from '../../components/WarningHasSubscription/WarningHasSubscription';
import UploadReelModal from '../../components/UploadReelModal/UploadReelModal';
import UploadVideoModal from '../../components/UploadVideoModal/UploadVideoModal';
import AddAdditionalImageModal from '../../components/AddAdditionalImageModal/AddAdditionalImageModal';
import UpdateSubscription from '../../components/UpdateSubscriptionForm/UpdateSubscriptionForm';
import ReelReviewModal from '../../components/ReelReviewModal/ReelReviewModal';
import VideoReviewModal from '../../components/VideoReviewModal/VideoReviewModal';

import ChangePassword from './forms/ChangePassword';
import EditCardDetails from './forms/EditCardDetails';
import EditPassword from './forms/EditPassword';
import RenewSubscription from './forms/RenewSubscription';
import CancelSubscription from './forms/CancelSubscription';
import ResetPassword from './forms/ResetPassword';
import Checkout from './forms/Checkout';
import ChooseOffer from './forms/ChooseOffer';
import PersonalDetails from './forms/PersonalDetails';
import Registration from './forms/Registration';
import Login from './forms/Login';
import { RecoveryUsername } from '../../components/ForgotPassword/RecoveryUsername';
import styles from './AccountModal.module.scss';
import Cancellation from './forms/Cancellation';

// @todo: connect with route typings
const PUBLIC_VIEWS = [
  'login',
  'create-account',
  'forgot-password',
  'reset-password',
  'send-confirmation',
  'edit-password',
  'simultaneous-logins',
  'reset-password-success',
  'reset-password-success-play',
  'recovery-username',
  'show-more-details',
];

export type AccountModals = {
  activate: 'activate';
  login: 'login';
  'create-account': 'create-account';
  'personal-details': 'personal-details';
  'choose-offer': 'choose-offer';
  'edit-card': 'edit-card';
  'upgrade-subscription': 'upgrade-subscription';
  'upgrade-subscription-error': 'upgrade-subscription-error';
  'upgrade-subscription-success': 'upgrade-subscription-success';
  'upgrade-subscription-pending': 'upgrade-subscription-pending';
  'change-password': 'change-password';
  checkout: 'checkout';
  'payment-error': 'payment-error';
  'payment-cancelled': 'payment-cancelled';
  'payment-success': 'payment-success';
  welcome: 'welcome';
  'reset-password': 'reset-password';
  'forgot-password': 'forgot-password';
  'add-password': 'add-password';
  'delete-account': 'delete-account';
  'delete-account-confirmation': 'delete-account-confirmation';
  'warning-account-deletion': 'warning-account-deletion';
  'send-confirmation': 'send-confirmation';
  'edit-password': 'edit-password';
  'reset-password-success': 'reset-password-success';
  'reset-password-success-play': 'reset-password-success-play';
  unsubscribe: 'unsubscribe';
  'renew-subscription': 'renew-subscription';
  'payment-method': 'payment-method';
  'payment-method-success': 'payment-method-success';
  'waiting-for-payment': 'waiting-for-payment';
  'finalize-payment': 'finalize-payment';
  'export-data': 'export-data';
  'export-data-success': 'export-data-success';
  'show-more-details': 'show-more-details';
  'cancel-upcoming-subscription-plan': 'cancel-upcoming-subscription-plan';
  'warning-active-subscribedItems': 'warning-active-subscribedItems';
  'warning-cancelled-subscribedItems': 'warning-cancelled-subscribedItems';
  'upload-reel': 'upload-reel';
  'upload-video': 'upload-video';
  'upload-success': 'upload-success';
  'add-additional-image': 'add-additional-image';
  'approval-success': 'approval-success';
  'approval-success-video': 'approval-success-video';
  'reel-review': 'reel-review';
  'reel-details': 'reel-details';
  'video-review': 'video-review';
  'video-details': 'video-details';
  'reject-reason': 'reject-reason';
  'rejection-success': 'rejection-success';
  'rejection-success-video': 'rejection-success-video';
  'recovery-username': 'recovery-username';
};

const AccountModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const viewParam = useQueryParam('u');
  const idParam = useQueryParam('id');
  const [view, setView] = useState<string | null>(viewParam ?? null);
  const message = useQueryParam('message');
  const { isAuthenticationEnabled, isShowLoginButton, isShowRegisterButton } = useAppConfig();
  const { loading, user } = useAccountStore(({ loading, user }) => ({ loading, user }), shallow);
  const config = useConfigStore((s) => s.config);
  const { isInitiated, reset: resetUploadStore } = useUploadStore();
  const { appConfig } = useConfigStore.getState();
  const { siteName } = config;
  const isPublicView = viewParam && PUBLIC_VIEWS.includes(viewParam);
  const banner = appConfig?.branding?.logo_url;
  const toLogin = useEventCallback(() => {
    navigate(modalURLFromLocation(location, 'login'));
  });
  const { t } = useTranslation(['account', 'upload']);
  const { subscriptionSuccessDescription, subscriptionFailedDescription, subscriptionCancelledDescription } = useUIControls();

  useEffect(() => {
    setView(viewParam ?? null);
  }, [viewParam]);

  useEffect(() => {
    if (!!viewParam && !loading && !user && !isPublicView) {
      // toLogin();
    }
  }, [viewParam, loading, isPublicView, user, toLogin]);

  useEffect(() => {
    // If we have upload/edit/review/details modal in URL but store shows it wasn't initiated by a user action,
    // it means it's a refresh or return from a refreshed preview.
    // Close it as requested.
    if ((viewParam === 'upload-reel' || viewParam === 'upload-video' || viewParam === 'add-additional-image') && !isInitiated) {
      navigate(createURLFromLocation(location, { u: null, message: null, id: null }));
    }
  }, [viewParam, isInitiated, navigate, location]);

  const closeHandler = useEventCallback(() => {
    if (view === 'upload-reel' || view === 'upload-video') {
      resetUploadStore();
    }

    if (view === 'welcome') {
      navigate('/');
    } else if (view === 'upload-success') {
      navigate(message === 'video' ? `${PATH_USER_VIDEOS}?tab=videos` : PATH_USER_VIDEOS, { replace: true });
    } else if (view === 'approval-success' || view === 'approval-success-video' || view === 'reel-review' || view === 'video-review' || view === 'rejection-success' || view === 'rejection-success-video') {
      navigate(PATH_USER_MODERATE, { replace: true });
    } else if (view === 'reject-reason') {
      navigate(createURLFromLocation(location, { u: message === 'video' ? 'video-review' : 'reel-review' }));
    } else if (view === 'add-additional-image') {
      navigate(createURLFromLocation(location, { u: 'upload-video', message: null, id: null }));
    } else if (view === 'reset-password-success-play') {
      navigate(PATH_SIGN_IN);
    } else {
      navigate(createURLFromLocation(location, { u: null, message: null, id: null }));
    }
  });

  const renderForm = () => {
    if (!user && loading && !isPublicView) {
      return (
        <div style={{ height: 300 }}>
          <LoadingOverlay inline />
        </div>
      );
    }

    switch (view) {
      case 'login':
        return isAuthenticationEnabled && (isShowLoginButton || isShowRegisterButton) ? <Login messageKey={message} /> : null;
      case 'create-account':
        return isAuthenticationEnabled && (isShowLoginButton || isShowRegisterButton) ? <Registration /> : null;
      case 'personal-details':
        return <PersonalDetails />;
      case 'choose-offer':
        return <ChooseOffer />;
      case 'edit-card':
        return <EditCardDetails setIsPaymentEdit={() => { }} isPaymentEdit={false} />;
      case 'upgrade-subscription':
        return <UpdateSubscription />;
      case 'upgrade-subscription-error':
        return <UpgradeSubscription type="error" onCloseButtonClick={closeHandler} />;
      case 'upgrade-subscription-success':
        return <UpgradeSubscription type="success" onCloseButtonClick={closeHandler} />;
      case 'upgrade-subscription-pending':
        return <UpgradeSubscription type="pending" onCloseButtonClick={closeHandler} />;
      case 'checkout':
        return <Checkout />;
      case 'payment-error':
        return (
          <PaymentPopup
            type="error"
            title={t('account:checkout.payment_error')}
            message={message ? message : subscriptionFailedDescription}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'payment-cancelled':
        return (
          <PaymentPopup
            type="cancelled"
            title={t('account:checkout.payment_cancelled')}
            message={message ? message : subscriptionCancelledDescription}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'payment-success':
        return (
          <PaymentPopup
            type="success"
            title={t('account:checkout.payment_success')}
            message={message ? message : subscriptionSuccessDescription}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'welcome':
        return <Welcome onCloseButtonClick={closeHandler} onCountdownCompleted={closeHandler} siteName={siteName} />;
      case 'reset-password':
        return <ResetPassword type="reset" />;
      case 'forgot-password':
        return <ResetPassword type="forgot" />;
      case 'add-password':
        return <EditPassword type="add" />;
      case 'delete-account':
      case 'delete-account-confirmation':
        return <DeleteAccountModal />;
      case 'warning-account-deletion':
        return <DeleteAccountPasswordWarning />;
      case 'send-confirmation':
        return <ResetPassword type="confirmation" />;
      case 'edit-password':
        return <EditPassword />;
      case 'reset-password-success':
        return <ResetPassword type="ResetFormSuccess" />;
      case 'unsubscribe':
        return <CancelSubscription />;
      case 'renew-subscription':
        return <RenewSubscription />;
      case 'payment-method':
      case 'payment-method-success':
        return <UpdatePaymentMethod onCloseButtonClick={closeHandler} />;
      case 'waiting-for-payment':
        return <WaitingForPayment />;
      case 'finalize-payment':
        return <FinalizePayment />;
      case 'export-data':
      case 'export-data-success':
        return <ExportData />;
      case 'reset-password-success-play':
        return (
          <PaymentPopup
            type="success"
            title={message ? t('account:reset.forgot_password') : t('account:reset.password_changed')}
            message={message || t('account:reset.password_changed_message')}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'show-more-details':
        return <ShowMoreDetails />;
      case 'change-password':
        return <ChangePassword type="confirmation" isChangePassword />;
      case 'cancel-upcoming-subscription-plan':
        return <Cancellation />;
      case 'warning-active-subscribedItems':
        return <WarningHasSubscription isActiveSubscription={true} />;
      case 'warning-cancelled-subscribedItems':
        return <WarningHasSubscription isActiveSubscription={false} />;
      case 'upload-reel':
        return <UploadReelModal />;
      case 'upload-video':
        return <UploadVideoModal />;
      case 'add-additional-image':
        return (
          <AddAdditionalImageModal
            key={idParam ?? 'new'}
            editIndex={idParam ? Number.parseInt(idParam, 10) : undefined}
            onSave={() => navigate(createURLFromLocation(location, { u: 'upload-video' }))}
          />
        );
      case 'upload-success':
        return (
          <PaymentPopup
            type="success"
            title={t('upload:success.title')}
            message={message === 'video' ? t('upload:success.video_description') : t('upload:success.description')}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'edit-success':
        return (
          <PaymentPopup
            type="success"
            title={t('upload:edit-success.title')}
            message={t('upload:edit-success.description')}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'delete-success':
        return (
          <PaymentPopup
            type="success"
            title={t('upload:delete-success.title')}
            message={t('upload:delete-success.description')}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'approval-success':
        return (
          <PaymentPopup
            type="success"
            title={t('upload:approval_success.title')}
            message={t('upload:approval_success.description')}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'approval-success-video':
        return (
          <PaymentPopup
            type="success"
            title={t('upload:approval-success-video.title')}
            message={t('upload:approval-success-video.description')}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'reel-review':
        return <ReelReviewModal maId={idParam ?? ''} />;
      case 'reel-details':
        return <ReelReviewModal maId={idParam ?? ''} viewOnly />;
      case 'video-review':
        return <VideoReviewModal maId={idParam ?? ''} />;
      // case 'video-details':
      //   return <VideoReviewModal maId={idParam ?? ''} viewOnly />;
      case 'reject-reason':
        return <RejectReasonModal />;
      case 'rejection-success':
        return (
          <PaymentPopup
            type="success"
            title={t('upload:rejection_success.title')}
            message={t('upload:rejection_success.description')}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'rejection-success-video':
        return (
          <PaymentPopup
            type="success"
            title={t('upload:rejection-success-video.title')}
            message={t('upload:rejection-success-video.description')}
            onCloseButtonClick={closeHandler}
          />
        );
      case 'recovery-username':
        return <RecoveryUsername onClose={closeHandler} />;
    }
  };

  const shouldShowBanner = ![
    'delete-account',
    'delete-account-confirmation',
    'edit-card',
    'warning-account-deletion',
    'export-data',
    'export-data-success',
    'show-more-details',
    'unsubscribe',
    'upgrade-subscription-success',
    'payment-error',
    'payment-cancelled',
    'payment-success',
    'cancel-upcoming-subscription-plan',
    'renew-subscription',
    'warning-active-subscribedItems',
    'warning-cancelled-subscribedItems',
    'upload-reel',
    'upload-video',
    'add-additional-image',
    'upload-success',
    'edit-success',
    'delete-success',
    'reel-review',
    'reel-details',
    'video-review',
    'approval-success',
    'approval-success-video',
    'reject-reason',
    'rejection-success',
    'rejection-success-video',
    'reset-password-success-play',
    'recovery-username',
  ].includes(view ?? '');
  const shouldNotCloseButton = ![
    'delete-account-confirmation',
    'export-data-success',
    'upgrade-subscription-success',
    'payment-error',
    'payment-cancelled',
    'payment-success',
    'warning-active-subscribedItems',
    'warning-cancelled-subscribedItems',
    'upload-success',
    'approval-success',
    'rejection-success',
    'reset-password-success-play',
    'recovery-username',
  ].includes(view ?? '');
  // const dialogSize = ['delete-account-confirmation'].includes(view ?? '') ? 'large' : 'small';

  const dialogSize =
    view === 'show-more-details' || view === 'upload-reel' || view === 'reel-review' || view === 'reel-details'
      ? 'large'
      : view === 'reject-reason' ? 'medium'
        : view === 'upload-video' || view === 'video-review' || view === 'video-details' ? 'extra-large'
          : view === 'choose-offer' || view === 'upgrade-subscription'
            ? 'subscription'
            : 'small';

  return (
    <Dialog size={dialogSize} shouldNotCloseButton={shouldNotCloseButton} open={!!viewParam} onClose={closeHandler} role="dialog">
      {shouldShowBanner && banner && <div className={styles.banner}>{<img src={appConfig?.branding?.logo_url} alt="" />}</div>}
      {renderForm()}
    </Dialog>
  );
};

export default AccountModal;
